import type { ProjectDocumentV3 } from "../storage/projectDocument";
import { assertProjectDocument } from "../storage/projectDocument";
import { ApiError, apiErrorFromResponse } from "./errors";
import type { components } from "./generated/schema";

export type SchedulingCandidate = components["schemas"]["SchedulingCandidate"];
export type SchedulingAlternativeHistory =
  components["schemas"]["SchedulingAlternativeHistory"];
export type SchedulingSearchMode = "STRICT" | "STRICT_THEN_MINIMAL_RELAXATION";

export type ConflictCertificate = components["schemas"]["ConflictCertificate"];
export type SchedulingPrecheckResult =
  components["schemas"]["SchedulingPrecheckResult"];

export interface CommandResponse<T> {
  document: ProjectDocumentV3;
  result: T;
  mutated: boolean;
}

export interface SchedulingJobFailure {
  code: string;
  message: string;
  field_errors: Array<Record<string, unknown>>;
  details: Record<string, unknown>;
}

export interface SchedulingJobStatus<T> {
  job_id: string;
  job_kind: "STRICT" | "REPAIR" | "PARTIAL" | "LEGACY";
  status: "QUEUED" | "RUNNING" | "COMPLETED" | "FAILED";
  submitted_at: string;
  started_at: string | null;
  completed_at: string | null;
  expires_at: string | null;
  estimated_wait_seconds: number | null;
  response: CommandResponse<T> | null;
  error: SchedulingJobFailure | null;
}

export interface RepairItem {
  repair_id: string;
  lock_key: string;
  category: string;
  title: string;
  original_value: string;
  proposed_value: string;
  degree: number;
  priority: number;
}

export interface RepairCandidate {
  candidate_kind: "REPAIR_COMPLETE" | "REPAIR_PARTIAL";
  solver_status: string;
  completeness: "COMPLETE" | "INCOMPLETE";
  repair_items: RepairItem[];
  assignments: Array<Record<string, unknown>>;
  plan_decisions: Array<Record<string, unknown>>;
  unassigned?: Array<Record<string, unknown>>;
  schedule_signature: string;
}

export interface RepairRoundStatus {
  round_id: string;
  round_number: number;
  round_kind: "FULL" | "PARTIAL";
  job_id: string;
  status:
    | "PENDING"
    | "RUNNING"
    | "FOUND"
    | "INFEASIBLE"
    | "UNKNOWN"
    | "REJECTED"
    | "ACCEPTED"
    | "FAILED"
    | "CANCELLED";
  profile_name: string | null;
  profile_signature: string;
  result: Record<string, unknown> | null;
  candidate: RepairCandidate | null;
  error: SchedulingJobFailure | null;
}

export function isRepairNoResultStatus(
  status: RepairRoundStatus["status"] | null | undefined
): boolean {
  return status === "INFEASIBLE" || status === "UNKNOWN" || status === "FAILED";
}

export interface RepairSessionStatus {
  session_id: string;
  source_strict_job_id: string;
  project_id: string;
  condition_revision: number;
  input_fingerprint: string;
  status: "ACTIVE" | "INVALIDATED" | "EXPIRED";
  completed_round_count: number;
  locked_repair_keys: string[];
  locked_repairs: Array<Record<string, unknown>>;
  partial_available: boolean;
  expires_at: string;
  current_round: RepairRoundStatus | null;
}

const SCHEDULING_REQUEST_TIMEOUT_MS = 10_000;

export async function executeDocumentCommand<T>(
  document: ProjectDocumentV3,
  commandType: string,
  payload: Record<string, unknown>,
  signal?: AbortSignal
): Promise<CommandResponse<T>> {
  const schedulingOperation = commandType.match(/^scheduling\.(preflight|generate)$/)?.[1];
  const endpoint = schedulingOperation
    ? `/api/v2/scheduling/${schedulingOperation}`
    : "/api/v2/project/commands";
  const requestBody = schedulingOperation
    ? {
        document,
        ...payload
      }
    : {
        document,
        expected_document_revision: document.document_revision,
        command: { type: commandType, payload }
      };
  const response = await statelessFetch(endpoint, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(requestBody),
    signal
  });
  if (!response.ok) throw await apiErrorFromResponse(response);
  const result = (await response.json()) as CommandResponse<T>;
  assertProjectDocument(result.document);
  return result;
}

export async function copyDocument(
  document: ProjectDocumentV3,
  newName: string,
  signal?: AbortSignal
): Promise<ProjectDocumentV3> {
  const response = await statelessFetch("/api/v2/project/copy", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ document, new_name: newName }),
    signal
  });
  if (!response.ok) throw await apiErrorFromResponse(response);
  const result = (await response.json()) as { document: ProjectDocumentV3 };
  assertProjectDocument(result.document);
  return result.document;
}

export async function submitSchedulingJob<T>(
  document: ProjectDocumentV3,
  jobId: string,
  accessToken: string,
  options: {
    searchMode: SchedulingSearchMode;
    alternativeHistory?: SchedulingAlternativeHistory;
  },
  signal?: AbortSignal
): Promise<SchedulingJobStatus<T>> {
  const response = await statelessFetch("/api/v2/scheduling/jobs", {
    method: "POST",
    headers: {
      "content-type": "application/json",
      "X-Scheduling-Job-Token": accessToken
    },
    body: JSON.stringify({
      job_id: jobId,
      job_kind: "STRICT",
      operation: "PRECHECK",
      document,
      expected_condition_revision: document.condition_revision,
      expected_timetable_revision: document.timetable_revision,
      relax_course_adjacency: false,
      search_mode: options.searchMode,
      alternative_history: options.alternativeHistory ?? null
    }),
    signal
  }, SCHEDULING_REQUEST_TIMEOUT_MS);
  if (!response.ok) throw await apiErrorFromResponse(response);
  return schedulingJobStatus<T>(await response.json());
}

export async function applyPrecheckedSchedule<T>(
  document: ProjectDocumentV3,
  candidate: SchedulingCandidate,
  applicationId: string,
  context: {
    relaxCourseAdjacency: false;
    searchMode: "STRICT";
    alternativeHistory: SchedulingAlternativeHistory | null;
  },
  signal?: AbortSignal
): Promise<CommandResponse<T>> {
  const response = await statelessFetch("/api/v2/scheduling/generate", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      document,
      expected_condition_revision: document.condition_revision,
      expected_timetable_revision: document.timetable_revision,
      candidate,
      application_id: applicationId,
      relax_course_adjacency: context.relaxCourseAdjacency,
      search_mode: context.searchMode,
      alternative_history: context.alternativeHistory
    }),
    signal
  });
  if (!response.ok) throw await apiErrorFromResponse(response);
  const result = (await response.json()) as CommandResponse<T>;
  assertProjectDocument(result.document);
  return result;
}

export async function candidateTimetable<T>(
  document: ProjectDocumentV3,
  candidate: SchedulingCandidate,
  viewSegment: "classes" | "teachers" | "rooms",
  objectId: string | null,
  weekParity: "ODD" | "EVEN",
  signal?: AbortSignal
): Promise<T> {
  const response = await statelessFetch(
    `/api/v2/project/timetables/${viewSegment}`,
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        document,
        candidate,
        object_id: objectId,
        week_parity: weekParity
      }),
      signal
    }
  );
  if (!response.ok) throw await apiErrorFromResponse(response);
  return (await response.json()) as T;
}

export async function getSchedulingJob<T>(
  jobId: string,
  accessToken: string,
  signal?: AbortSignal
): Promise<SchedulingJobStatus<T>> {
  const response = await statelessFetch(`/api/v2/scheduling/jobs/${jobId}`, {
    headers: { "X-Scheduling-Job-Token": accessToken },
    signal
  }, SCHEDULING_REQUEST_TIMEOUT_MS);
  if (!response.ok) throw await apiErrorFromResponse(response);
  return schedulingJobStatus<T>(await response.json());
}

export async function acknowledgeSchedulingJob(
  jobId: string,
  accessToken: string,
  signal?: AbortSignal
): Promise<void> {
  const response = await statelessFetch(`/api/v2/scheduling/jobs/${jobId}`, {
    method: "DELETE",
    headers: { "X-Scheduling-Job-Token": accessToken },
    signal
  });
  if (!response.ok) throw await apiErrorFromResponse(response);
}

export async function createRepairSession(
  sourceStrictJobId: string,
  sessionId: string,
  roundId: string,
  jobId: string,
  accessToken: string,
  signal?: AbortSignal
): Promise<RepairSessionStatus> {
  return repairSessionRequest("/api/v2/scheduling/repair-sessions", accessToken, {
    method: "POST",
    body: JSON.stringify({
      session_id: sessionId,
      round_id: roundId,
      job_id: jobId,
      source_strict_job_id: sourceStrictJobId
    }),
    signal
  });
}

export async function getRepairSession(
  sessionId: string,
  accessToken: string,
  signal?: AbortSignal
): Promise<RepairSessionStatus> {
  return repairSessionRequest(
    `/api/v2/scheduling/repair-sessions/${sessionId}`,
    accessToken,
    { signal }
  );
}

export async function createRepairRound(
  sessionId: string,
  accessToken: string,
  payload: {
    round_id: string;
    job_id: string;
    previous_round_id: string;
    action: "RETRY" | "REJECT_AND_RETRY";
    locked_repair_keys: string[];
  },
  signal?: AbortSignal
): Promise<RepairSessionStatus> {
  return repairSessionRequest(
    `/api/v2/scheduling/repair-sessions/${sessionId}/rounds`,
    accessToken,
    { method: "POST", body: JSON.stringify(payload), signal }
  );
}

export async function createPartialRepairRound(
  sessionId: string,
  accessToken: string,
  roundId: string,
  jobId: string,
  signal?: AbortSignal
): Promise<RepairSessionStatus> {
  return repairSessionRequest(
    `/api/v2/scheduling/repair-sessions/${sessionId}/partial-rounds`,
    accessToken,
    {
      method: "POST",
      body: JSON.stringify({ round_id: roundId, job_id: jobId }),
      signal
    }
  );
}

export async function previewRepairCandidate<T>(
  sessionId: string,
  roundId: string,
  accessToken: string,
  viewSegment: "classes" | "teachers" | "rooms",
  objectId: string | null,
  weekParity: "ODD" | "EVEN",
  signal?: AbortSignal
): Promise<T> {
  const response = await statelessFetch(
    `/api/v2/scheduling/repair-sessions/${sessionId}/preview/${viewSegment}`,
    {
      method: "POST",
      headers: {
        "content-type": "application/json",
        "X-Scheduling-Session-Token": accessToken
      },
      body: JSON.stringify({
        round_id: roundId,
        object_id: objectId,
        week_parity: weekParity
      }),
      signal
    }
  );
  if (!response.ok) throw await apiErrorFromResponse(response);
  return (await response.json()) as T;
}

export async function acceptRepairCandidate<T>(
  sessionId: string,
  roundId: string,
  applicationId: string,
  accessToken: string,
  document: ProjectDocumentV3,
  signal?: AbortSignal
): Promise<CommandResponse<T>> {
  const response = await statelessFetch(
    `/api/v2/scheduling/repair-sessions/${sessionId}/accept`,
    {
      method: "POST",
      headers: {
        "content-type": "application/json",
        "X-Scheduling-Session-Token": accessToken
      },
      body: JSON.stringify({
        round_id: roundId,
        application_id: applicationId,
        document,
        expected_condition_revision: document.condition_revision,
        expected_document_revision: document.document_revision
      }),
      signal
    }
  );
  if (!response.ok) throw await apiErrorFromResponse(response);
  const result = (await response.json()) as CommandResponse<T>;
  assertProjectDocument(result.document);
  return result;
}

export async function acknowledgeRepairAcceptance(
  sessionId: string,
  applicationId: string,
  accessToken: string,
  signal?: AbortSignal
): Promise<void> {
  const response = await statelessFetch(
    `/api/v2/scheduling/repair-sessions/${sessionId}/accept/ack`,
    {
      method: "POST",
      headers: {
        "content-type": "application/json",
        "X-Scheduling-Session-Token": accessToken
      },
      body: JSON.stringify({ application_id: applicationId }),
      signal
    }
  );
  if (!response.ok) throw await apiErrorFromResponse(response);
}

export async function invalidateRepairSession(
  sessionId: string,
  accessToken: string,
  signal?: AbortSignal
): Promise<void> {
  const response = await statelessFetch(
    `/api/v2/scheduling/repair-sessions/${sessionId}/invalidate`,
    {
      method: "POST",
      headers: { "X-Scheduling-Session-Token": accessToken },
      signal
    }
  );
  if (!response.ok) throw await apiErrorFromResponse(response);
}

export interface ExportDownload {
  blob: Blob;
  filename: string;
}

export async function exportDocument(
  document: ProjectDocumentV3,
  payload: Record<string, unknown>,
  signal?: AbortSignal
): Promise<ExportDownload> {
  const response = await statelessFetch("/api/v2/exports/xlsx", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ document, ...payload }),
    signal
  });
  if (!response.ok) throw await apiErrorFromResponse(response);
  const disposition = response.headers.get("content-disposition") ?? "";
  const encodedName = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  return {
    blob: await response.blob(),
    filename: encodedName ? decodeURIComponent(encodedName) : "课表.xlsx"
  };
}

export async function downloadPlanningImportTemplate(
  document: ProjectDocumentV3,
  signal?: AbortSignal
): Promise<ExportDownload> {
  const response = await statelessFetch("/api/v2/planning-imports/template", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ document }),
    signal
  });
  if (!response.ok) throw await apiErrorFromResponse(response);
  const disposition = response.headers.get("content-disposition") ?? "";
  const encodedName = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  return {
    blob: await response.blob(),
    filename: encodedName
      ? decodeURIComponent(encodedName)
      : "课程需求与教师分配导入模板.xlsx"
  };
}

export async function statelessRequest<T>(path: string, signal?: AbortSignal): Promise<T> {
  const response = await statelessFetch(`/api/v2${path}`, { signal });
  if (!response.ok) throw await apiErrorFromResponse(response);
  return (await response.json()) as T;
}

async function statelessFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
  timeoutMs?: number
): Promise<Response> {
  const callerSignal = init?.signal;
  const timeoutController = timeoutMs ? new AbortController() : null;
  let timedOut = false;
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  const abortFromCaller = (): void => timeoutController?.abort();
  if (timeoutController) {
    if (callerSignal?.aborted) {
      timeoutController.abort();
    } else {
      callerSignal?.addEventListener("abort", abortFromCaller, { once: true });
    }
    timeoutId = setTimeout(() => {
      timedOut = true;
      timeoutController.abort();
    }, timeoutMs);
  }
  try {
    return await fetch(
      input,
      timeoutController ? { ...init, signal: timeoutController.signal } : init
    );
  } catch (error) {
    if (timedOut && error instanceof DOMException && error.name === "AbortError") {
      throw new ApiError({
        error: {
          code: "SCHEDULING_REQUEST_TIMEOUT",
          message: "排课任务暂未确认，请重新尝试；重复尝试不会创建新任务",
          field_errors: [],
          details: {},
          request_id: "client"
        }
      });
    }
    if (error instanceof DOMException && error.name === "AbortError") throw error;
    throw new ApiError({
      error: {
        code: "SERVICE_UNAVAILABLE",
        message: "计算服务暂时不可用，本地课表未被改动，请稍后重试",
        field_errors: [],
        details: {},
        request_id: "client"
      }
    });
  } finally {
    if (timeoutId !== null) clearTimeout(timeoutId);
    callerSignal?.removeEventListener("abort", abortFromCaller);
  }
}

function schedulingJobStatus<T>(value: unknown): SchedulingJobStatus<T> {
  if (!value || typeof value !== "object") {
    throw new ApiError({
      error: {
        code: "DOCUMENT_INVALID",
        message: "排课服务返回了无效的任务状态",
        field_errors: [],
        details: {},
        request_id: "client"
      }
    });
  }
  const status = value as SchedulingJobStatus<T>;
  if (status.response) assertProjectDocument(status.response.document);
  return status;
}

async function repairSessionRequest(
  path: string,
  accessToken: string,
  init: RequestInit
): Promise<RepairSessionStatus> {
  const response = await statelessFetch(path, {
    ...init,
    headers: {
      "content-type": "application/json",
      "X-Scheduling-Session-Token": accessToken,
      ...init.headers
    }
  });
  if (!response.ok) throw await apiErrorFromResponse(response);
  const value = (await response.json()) as RepairSessionStatus;
  if (!value || typeof value !== "object" || typeof value.session_id !== "string") {
    throw new ApiError({
      error: {
        code: "DOCUMENT_INVALID",
        message: "排课服务返回了无效的调整方案状态",
        field_errors: [],
        details: {},
        request_id: "client"
      }
    });
  }
  return value;
}
